import type { ModuleId, Question } from '../../../../../../content';
import { getFamiliesForModule, getAllowedTiersForGroup, MODULES } from '../../../../../../content';
import { GROUPS, type GroupId } from '../../../../../../config/systemConfig';
export type PracticeCapacityStatus = 'critical' | 'thin' | 'healthy';
export const PRACTICE_MINIMUM_VARIANTS = 7;
export const PRACTICE_SESSION_SIZE = 10;
export interface PracticeFamilyCapacity {
  key: string;
  group: GroupId;
  module: ModuleId;
  familyId: string;
  questionCount: number;
  questionTypes: string[];
  status: PracticeCapacityStatus;
  questionsNeeded: number;
  sessionRepeatCount: number;
}
export interface PracticeModuleCapacity {
  group: GroupId;
  module: ModuleId;
  eligibleFamilies: number;
  familiesWithContent: number;
  totalQuestions: number;
  criticalFamilies: number;
  thinFamilies: number;
  status: PracticeCapacityStatus;
}
export interface PracticeContentHealthReport {
  families: PracticeFamilyCapacity[];
  modules: PracticeModuleCapacity[];
  counts: {
    critical: number;
    thin: number;
    healthy: number;
  };
}

/**
 * Practice capacity is intentionally derived from taxonomy plus loaded content,
 * rather than gate results: a family with zero questions must remain visible.
 * It describes routing resilience, never learner mastery or eligibility.
 */
export function computePracticeContentHealth(questions: readonly Question[]): PracticeContentHealthReport {
  const families: PracticeFamilyCapacity[] = [];
  const modules: PracticeModuleCapacity[] = [];
  for (const group of GROUPS) {
    const allowedTiers = getAllowedTiersForGroup(group);
    for (const module of MODULES) {
      const eligibleFamilies = getFamiliesForModule(module.id).filter(family => allowedTiers.has(family.tier));
      const moduleFamilies = eligibleFamilies.map(family => {
        const usable = questions.filter(question => question.familyId === family.familyId && question.module === module.id && allowedTiers.has(question.tier));
        const questionTypes = Array.from(new Set(usable.map(question => question.questionType))).sort();
        const status = classifyPracticeCapacity(usable.length);
        return {
          key: `${group}::${family.familyId}`,
          group,
          module: module.id,
          familyId: family.familyId,
          questionCount: usable.length,
          questionTypes,
          status,
          questionsNeeded: Math.max(0, PRACTICE_MINIMUM_VARIANTS - usable.length),
          sessionRepeatCount: Math.max(0, PRACTICE_SESSION_SIZE - usable.length)
        };
      });
      families.push(...moduleFamilies);
      const totalQuestions = moduleFamilies.reduce((sum, family) => sum + family.questionCount, 0);
      const criticalFamilies = moduleFamilies.filter(family => family.status === 'critical').length;
      const thinFamilies = moduleFamilies.filter(family => family.status === 'thin').length;
      modules.push({
        group,
        module: module.id,
        eligibleFamilies: moduleFamilies.length,
        familiesWithContent: moduleFamilies.filter(family => family.questionCount > 0).length,
        totalQuestions,
        criticalFamilies,
        thinFamilies,
        status: criticalFamilies > 0 ? 'critical' : thinFamilies > 0 ? 'thin' : 'healthy'
      });
    }
  }
  families.sort((a, b) => {
    const rank = {
      critical: 2,
      thin: 1,
      healthy: 0
    };
    const statusDiff = rank[b.status] - rank[a.status];
    if (statusDiff !== 0) return statusDiff;
    if (a.questionCount !== b.questionCount) return a.questionCount - b.questionCount;
    return a.familyId.localeCompare(b.familyId);
  });
  return {
    families,
    modules,
    counts: {
      critical: families.filter(family => family.status === 'critical').length,
      thin: families.filter(family => family.status === 'thin').length,
      healthy: families.filter(family => family.status === 'healthy').length
    }
  };
}
function classifyPracticeCapacity(count: number): PracticeCapacityStatus {
  if (count < 4) return 'critical';
  if (count < PRACTICE_MINIMUM_VARIANTS) return 'thin';
  return 'healthy';
}